/** @jsx jsx */
import { jsx, useColorMode } from 'theme-ui';

const modeColors = {
  light: { from: '#111', to: '#999' },
  black: { from: '#f8f8f8', to: '#555' },
  dark: { from: '#f5f5f5', to: '#666' },
  deep: { from: 'hsl(260, 98%, 82%)', to: 'hsl(290, 98%, 82%)' },
  hack: { from: '#48bb78', to: '#9ae6b4' },
  pink: { from: '#b91c1c', to: '#ed64a6' },
};

const Sphere = ({ clipId, paths }) => {
  const [colorMode] = useColorMode();
  const { from, to } = modeColors[colorMode] || modeColors.light;
  const gradientId = `grad-${clipId}`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 200"
      width="100%"
      height="100%"
      sx={{
        filter:
          'drop-shadow(0px 2px 4px rgba(0,0,0,0.15)) drop-shadow(0px 4px 8px rgba(0,0,0,0.1))',
      }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={from} />
          <stop offset="100%" stopColor={to} />
        </linearGradient>
        <clipPath id={clipId}>
          <circle cx="100" cy="100" r="96" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="8"
            strokeLinejoin="round"
            strokeOpacity="0.55"
          />
        ))}
      </g>
    </svg>
  );
};

// App icon — welcome variant, scaled to 200x200
const iconPaths = [
  'M67.8,50.6L118.8,53.4L143.8,39.4L108.4,27.8L61.3,34.7L67.8,50.6Z',
  'M34.4,76.9L61.3,34.7L108.4,27.8L114.4,65.3L68.4,90.9L34.4,76.9Z',
  'M154.7,95.9L114.4,65.3L108.4,27.8L143.8,39.4L174.4,85.3L154.7,95.9Z',
  'M165.6,123.1L174.4,85.3L143.8,39.4L118.8,53.4L131.6,109.1L165.6,123.1Z',
  'M45.3,104.1L85.6,134.7L131.6,109.1L118.8,53.4L67.8,50.6L45.3,104.1Z',
  'M45.3,104.1L67.8,50.6L61.3,34.7L34.4,76.9L25.6,114.7L45.3,104.1Z',
  'M81.3,146.6L68.4,90.9L114.4,65.3L154.7,95.9L132.2,149.4L81.3,146.6Z',
  'M138.8,165.3L132.2,149.4L154.7,95.9L174.4,85.3L165.6,123.1L138.8,165.3Z',
  'M91.6,172.2L138.8,165.3L165.6,123.1L131.6,109.1L85.6,134.7L91.6,172.2Z',
  'M91.6,172.2L85.6,134.7L45.3,104.1L25.6,114.7L56.3,160.6L91.6,172.2Z',
  'M81.3,146.6L56.3,160.6L25.6,114.7L34.4,76.9L68.4,90.9L81.3,146.6Z',
  'M91.6,172.2L56.3,160.6L81.3,146.6L132.2,149.4L138.8,165.3L91.6,172.2Z',
];

// Spanish B1 — icosahedron, rotation [-10, 45, 0]
const spanishPaths = [
  'M61.0,70.9L107.7,87.0L143.7,60.6L119.4,28.2L68.2,34.6L61.0,70.9Z',
  'M46.1,54.4L68.2,34.6L119.4,28.2L132.9,43.6L87.0,56.6L46.1,54.4Z',
  'M166.2,83.9L132.9,43.6L119.4,28.2L143.7,60.6L174.3,98.6L166.2,83.9Z',
  'M153.9,145.6L174.3,98.6L143.7,60.6L107.7,87.0L113.0,143.4L153.9,145.6Z',
  'M33.8,116.1L67.1,156.4L113.0,143.4L107.7,87.0L61.0,70.9L33.8,116.1Z',
  'M33.8,116.1L61.0,70.9L68.2,34.6L46.1,54.4L25.7,101.4L33.8,116.1Z',
  'M92.3,113.0L87.0,56.6L132.9,43.6L166.2,83.9L139.0,129.1L92.3,113.0Z',
  'M131.8,165.4L139.0,129.1L166.2,83.9L174.3,98.6L153.9,145.6L131.8,165.4Z',
  'M80.6,171.8L131.8,165.4L153.9,145.6L113.0,143.4L67.1,156.4L80.6,171.8Z',
  'M80.6,171.8L67.1,156.4L33.8,116.1L25.7,101.4L56.3,139.4L80.6,171.8Z',
  'M92.3,113.0L56.3,139.4L25.7,101.4L46.1,54.4L87.0,56.6L92.3,113.0Z',
  'M80.6,171.8L56.3,139.4L92.3,113.0L139.0,129.1L131.8,165.4L80.6,171.8Z',
];

// Python — fibonacci(24), rotation [-35, 120, 10]
const pythonPaths = [
  'M105.1,94.1L64.2,94.9L60.8,101.3L80.6,149.4L127.5,137.1L105.1,94.1Z',
  'M124.9,170.9L134.5,138.3L127.5,137.1L80.6,149.4L72.2,159.9L104.4,175.8L124.9,170.9Z',
  'M124.5,66.6L105.1,94.1L127.5,137.1L134.5,138.3L154.6,124.5L150.3,70.2L124.5,66.6Z',
  'M44.9,149.0L72.2,159.9L80.6,149.4L60.8,101.3L29.9,117.4L44.9,149.0Z',
  'M170.7,124.3L154.6,124.5L134.5,138.3L124.9,170.9L157.8,146.9L170.7,124.3Z',
  'M124.5,66.6L92.0,38.6L59.9,63.3L64.2,94.9L105.1,94.1L124.5,66.6Z',
  'M93.8,173.2L104.4,175.8L72.2,159.9L44.9,149.0L55.3,155.3L93.8,173.2Z',
  'M170.7,124.3L173.4,80.9L157.1,60.8L150.3,70.2L154.6,124.5L170.7,124.3Z',
  'M35.2,62.5L25.1,105.2L29.9,117.4L60.8,101.3L64.2,94.9L59.9,63.3L35.2,62.5Z',
  'M154.7,143.3L157.8,146.9L124.9,170.9L104.4,175.8L93.8,173.2L118.8,151.4L154.7,143.3Z',
  'M94.8,31.0L92.0,38.6L124.5,66.6L150.3,70.2L157.1,60.8L129.4,30.4L94.8,31.0Z',
  'M51.5,144.8L55.3,155.3L44.9,149.0L29.9,117.4L25.1,105.2L32.8,110.7L51.5,144.8Z',
  'M169.6,77.6L173.4,80.9L170.7,124.3L157.8,146.9L154.7,143.3L160.0,99.7L169.6,77.6Z',
  'M41.8,51.2L35.2,62.5L59.9,63.3L92.0,38.6L94.8,31.0L65.3,32.6L41.8,51.2Z',
  'M106.7,133.9L118.8,151.4L93.8,173.2L55.3,155.3L51.5,144.8L85.2,128.6L106.7,133.9Z',
  'M169.6,77.6L133.1,38.7L128.7,34.6L129.4,30.4L157.1,60.8L173.4,80.9L169.6,77.6Z',
  'M45.8,79.9L32.8,110.7L25.1,105.2L35.2,62.5L41.8,51.2L45.8,79.9Z',
  'M133.1,92.6L160.0,99.7L154.7,143.3L118.8,151.4L106.7,133.9L133.1,92.6Z',
  'M87.5,32.8L65.3,32.6L94.8,31.0L129.4,30.4L128.7,34.6L87.5,32.8Z',
  'M66.9,75.8L85.2,128.6L51.5,144.8L32.8,110.7L45.8,79.9L66.9,75.8Z',
  'M121.8,74.4L133.1,38.7L169.6,77.6L160.0,99.7L133.1,92.6L121.8,74.4Z',
  'M73.2,70.3L66.9,75.8L45.8,79.9L41.8,51.2L65.3,32.6L87.5,32.8L73.2,70.3Z',
  'M121.8,74.4L133.1,92.6L106.7,133.9L85.2,128.6L66.9,75.8L73.2,70.3L121.8,74.4Z',
  'M121.8,74.4L73.2,70.3L87.5,32.8L128.7,34.6L133.1,38.7L121.8,74.4Z',
];

// Biology 101 — spiral(30), rotation [-20, 200, 0]
const biologyPaths = [
  'M91.2,164.6L105.6,166.6L108.0,170.4L85.4,174.2L78.4,168.5L91.2,164.6Z',
  'M117.0,173.8L86.6,174.8L85.4,174.2L108.0,170.4L126.3,171.0L117.0,173.8Z',
  'M131.2,153.3L143.6,158.7L126.3,171.0L108.0,170.4L105.6,166.6L131.2,153.3Z',
  'M90.2,142.0L125.5,137.4L131.2,153.3L105.6,166.6L91.2,164.6L90.2,142.0Z',
  'M50.2,148.5L69.5,129.1L90.2,142.0L91.2,164.6L78.4,168.5L50.2,148.5Z',
  'M63.1,162.5L36.3,140.1L50.2,148.5L78.4,168.5L85.4,174.2L86.6,174.8L63.1,162.5Z',
  'M122.7,162.0L71.1,152.5L63.1,162.5L86.6,174.8L117.0,173.8L122.7,162.0Z',
  'M164.5,139.5L143.0,143.4L122.7,162.0L117.0,173.8L126.3,171.0L143.6,158.7L164.5,139.5Z',
  'M137.5,112.1L173.2,112.2L164.5,139.5L143.6,158.7L131.2,153.3L125.5,137.4L137.5,112.1Z',
  'M65.7,104.3L124.9,84.2L137.5,112.1L125.5,137.4L90.2,142.0L69.5,129.1L65.7,104.3Z',
  'M26.8,119.7L48.4,82.2L65.7,104.3L69.5,129.1L50.2,148.5L36.3,140.1L26.8,119.7Z',
  'M26.8,100.8L26.8,119.7L36.3,140.1L63.1,162.5L71.1,152.5L66.2,135.5L26.8,100.8Z',
  'M83.0,112.2L66.2,135.5L71.1,152.5L122.7,162.0L143.0,143.4L143.7,127.0L83.0,112.2Z',
  'M155.2,97.2L143.7,127.0L143.0,143.4L164.5,139.5L173.2,112.2L175.4,95.5L155.2,97.2Z',
  'M166.2,65.7L175.4,95.5L173.2,112.2L137.5,112.1L124.9,84.2L126.7,67.3L166.2,65.7Z',
  'M107.9,45.3L126.7,67.3L124.9,84.2L65.7,104.3L48.4,82.2L51.2,64.9L107.9,45.3Z',
  'M30.2,81.8L46.4,50.2L51.2,64.9L48.4,82.2L26.8,119.7L26.8,100.8L30.2,81.8Z',
  'M82.3,91.2L46.3,67.1L30.2,81.8L26.8,100.8L66.2,135.5L83.0,112.2L82.3,91.2Z',
  'M146.4,76.1L100.8,71.4L82.3,91.2L83.0,112.2L143.7,127.0L155.2,97.2L146.4,76.1Z',
  'M155.2,47.7L146.0,55.0L146.4,76.1L155.2,97.2L175.4,95.5L166.2,65.7L155.2,47.7Z',
  'M136.0,33.3L155.2,47.7L166.2,65.7L126.7,67.3L107.9,45.3L109.1,30.1L136.0,33.3Z',
  'M91.1,25.7L109.1,30.1L107.9,45.3L51.2,64.9L46.4,50.2L66.2,32.1L91.1,25.7Z',
  'M68.4,34.0L66.2,32.1L46.4,50.2L30.2,81.8L46.3,67.1L67.3,40.9L68.4,34.0Z',
  'M95.4,42.2L67.3,40.9L46.3,67.1L82.3,91.2L100.8,71.4L95.4,42.2Z',
  'M99.3,40.1L95.4,42.2L100.8,71.4L146.4,76.1L146.0,55.0L116.5,36.3L99.3,40.1Z',
  'M116.1,28.5L116.5,36.3L146.0,55.0L155.2,47.7L136.0,33.3L116.1,28.5Z',
  'M103.0,25.4L116.1,28.5L136.0,33.3L109.1,30.1L91.1,25.7L103.0,25.4Z',
  'M95.0,26.8L103.0,25.4L91.1,25.7L66.2,32.1L68.4,34.0L95.0,26.8Z',
  'M95.0,26.8L68.4,34.0L67.3,40.9L95.4,42.2L99.3,40.1L95.0,26.8Z',
  'M95.0,26.8L99.3,40.1L116.5,36.3L116.1,28.5L103.0,25.4L95.0,26.8Z',
];

// World History — latitude(4,5), rotation [-45, 270, 5]
const historyPaths = [
  'M79.6,119.7L75.0,86.7L104.9,72.1L128.0,96.1L112.4,125.5L79.6,119.7Z',
  'M72.5,106.9L98.0,128.3L126.3,110.6L118.2,78.3L85.0,76.0L72.5,106.9Z',
  'M95.0,171.1L60.2,138.5L79.6,119.7L112.4,125.5L124.3,149.8L95.0,171.1Z',
  'M166.1,126.7L124.3,149.8L112.4,125.5L128.0,96.1L154.8,92.3L166.1,126.7Z',
  'M145.8,45.4L154.8,92.3L128.0,96.1L104.9,72.1L109.6,45.5L145.8,45.4Z',
  'M62.2,39.5L109.6,45.5L104.9,72.1L75.0,86.7L51.1,74.0L62.2,39.5Z',
  'M30.8,117.2L51.1,74.0L75.0,86.7L79.6,119.7L60.2,138.5L30.8,117.2Z',
  'M47.4,150.8L29.1,117.7L30.8,117.2L60.2,138.5L95.0,171.1L94.9,172.9L47.4,150.8Z',
  'M132.0,165.7L94.9,172.9L95.0,171.1L124.3,149.8L166.1,126.7L167.8,127.4L132.0,165.7Z',
  'M172.4,89.8L167.8,127.4L166.1,126.7L154.8,92.3L145.8,45.4L147.0,44.0L172.4,89.8Z',
  'M112.7,28.0L147.0,44.0L145.8,45.4L109.6,45.5L62.2,39.5L61.3,38.0L112.7,28.0Z',
  'M35.5,65.7L61.3,38.0L62.2,39.5L51.1,74.0L30.8,117.2L29.1,117.7L35.5,65.7Z',
  'M96.1,155.2L48.7,149.5L47.4,150.8L94.9,172.9L132.0,165.7L131.2,164.1L96.1,155.2Z',
  'M151.3,120.7L131.2,164.1L132.0,165.7L167.8,127.4L172.4,89.8L170.6,90.1L151.3,120.7Z',
  'M135.6,57.6L170.6,90.1L172.4,89.8L147.0,44.0L112.7,28.0L112.4,29.8L135.6,57.6Z',
  'M70.7,53.1L112.4,29.8L112.7,28.0L61.3,38.0L35.5,65.7L37.1,66.5L70.7,53.1Z',
  'M46.3,113.4L37.1,66.5L35.5,65.7L29.1,117.7L47.4,150.8L48.7,149.5L46.3,113.4Z',
  'M72.5,106.9L46.3,113.4L48.7,149.5L96.1,155.2L98.0,128.3L72.5,106.9Z',
  'M126.3,110.6L98.0,128.3L96.1,155.2L131.2,164.1L151.3,120.7L126.3,110.6Z',
  'M126.3,110.6L151.3,120.7L170.6,90.1L135.6,57.6L118.2,78.3L126.3,110.6Z',
  'M85.0,76.0L118.2,78.3L135.6,57.6L112.4,29.8L70.7,53.1L85.0,76.0Z',
  'M72.5,106.9L85.0,76.0L70.7,53.1L37.1,66.5L46.3,113.4L72.5,106.9Z',
];

// First Aid — icosahedron, rotation [-15, 330, 0]
const firstaidPaths = [
  'M63.0,34.4L111.4,25.1L144.1,45.3L115.8,67.0L65.7,60.3L63.0,34.4Z',
  'M41.7,112.9L65.7,60.3L115.8,67.0L126.9,124.3L80.6,147.0L41.7,112.9Z',
  'M162.9,131.2L126.9,124.3L115.8,67.0L144.1,45.3L174.9,87.3L162.9,131.2Z',
  'M158.3,87.1L174.9,87.3L144.1,45.3L111.4,25.1L119.4,53.0L158.3,87.1Z',
  'M37.1,68.8L73.1,75.7L119.4,53.0L111.4,25.1L63.0,34.4L37.1,68.8Z',
  'M37.1,68.8L63.0,34.4L65.7,60.3L41.7,112.9L25.1,112.7L37.1,68.8Z',
  'M88.6,174.9L80.6,147.0L126.9,124.3L162.9,131.2L137.0,165.6L88.6,174.9Z',
  'M134.3,139.7L137.0,165.6L162.9,131.2L174.9,87.3L158.3,87.1L134.3,139.7Z',
  'M84.2,133.0L134.3,139.7L158.3,87.1L119.4,53.0L73.1,75.7L84.2,133.0Z',
  'M84.2,133.0L73.1,75.7L37.1,68.8L25.1,112.7L55.9,154.7L84.2,133.0Z',
  'M88.6,174.9L55.9,154.7L25.1,112.7L41.7,112.9L80.6,147.0L88.6,174.9Z',
  'M84.2,133.0L55.9,154.7L88.6,174.9L137.0,165.6L134.3,139.7L84.2,133.0Z',
];

const topics = [
  { id: 'icon', paths: iconPaths },
  { id: 'spanish', paths: spanishPaths },
  { id: 'firstaid', paths: firstaidPaths },
  { id: 'python', paths: pythonPaths },
  { id: 'biology', paths: biologyPaths },
  { id: 'history', paths: historyPaths },
];

const ProjectCard = ({ title, description, children }) => (
  <div
    sx={{
      p: 0,
      mb: 4,
    }}
  >
    <div sx={{ mb: 3 }}>
      <h4 sx={{ fontWeight: 600, m: 0 }}>{title}</h4>
      <p sx={{ fontSize: 1 }}>{description}</p>
    </div>
    {children}
  </div>
);

const Projects = () => {
  return (
    <div>
      <h2
        sx={{
          fontSize: 3,
          fontWeight: 700,
          mb: 3,
          '&:first-of-type': { mt: 0 },
        }}
      >
        Projects
      </h2>

      <ProjectCard
        title="Hashcards App"
        description="Thoughts on building a modern spaced repetition app, learning science, and the journey of Hashcards App."
      >
        <div
          sx={{
            display: 'grid',
            gridTemplateColumns: ['repeat(3, 1fr)', 'repeat(6, 1fr)'],
            gap: 3,
            mb: 4,
          }}
        >
          {topics.map((topic) => (
            <div
              key={topic.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <Sphere clipId={`sphere-${topic.id}`} paths={topic.paths} />
            </div>
          ))}
        </div>
        <ul sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {[
            {
              href: '/blog/the-hashcards-journey',
              subtitle: 'A Modern Take on Spaced Repetition',
              date: '2026-02-01',
            },
            {
              href: '/blog/how-hashcards-works',
              subtitle: 'The Technical Architecture',
              date: '2026-02-01',
            },
            {
              href: '/blog/this-week-in-hashcards-app-01',
              subtitle: 'Your Memory Stats, Decoded',
              date: '2026-02-10',
            },
            {
              href: '/blog/this-week-in-hashcards-app-02',
              subtitle: 'Bring Your Own Model & New Generation Flow',
              date: '2026-03-27',
            },
          ].map((post) => (
            <li
              key={post.href}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
              }}
            >
              <a
                href={`https://hashcards.app${post.href}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  variant: 'styles.navlink',
                  textDecoration: 'none',
                  fontSize: 2,
                  fontWeight: 'body',
                  ':hover': { textDecoration: 'underline' },
                }}
              >
                {post.subtitle}
              </a>
              <span
                sx={{
                  fontSize: 1,
                  color: 'gray',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {post.date}
              </span>
            </li>
          ))}
        </ul>

        <div sx={{ display: 'flex', gap: 3, mt: 3 }}>
          <a
            href="https://apps.apple.com/app/hashcards/id6756789647"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              textDecoration: 'none',
              color: 'background',
              bg: 'text',
              px: 3,
              py: 2,
              borderRadius: 8,
            }}
          >
            <svg
              sx={{ width: 20, height: 20 }}
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div sx={{ textAlign: 'left' }}>
              <div sx={{ fontSize: 0, opacity: 0.8, lineHeight: 1 }}>
                Download on the
              </div>
              <div sx={{ fontSize: 1, fontWeight: 600, lineHeight: 1.2 }}>
                App Store
              </div>
            </div>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=app.hashcards.mobile"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              textDecoration: 'none',
              color: 'background',
              bg: 'text',
              px: 3,
              py: 2,
              borderRadius: 8,
            }}
          >
            <svg
              sx={{ width: 20, height: 20 }}
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
            </svg>
            <div sx={{ textAlign: 'left' }}>
              <div sx={{ fontSize: 0, opacity: 0.8, lineHeight: 1 }}>
                Get it on
              </div>
              <div sx={{ fontSize: 1, fontWeight: 600, lineHeight: 1.2 }}>
                Google Play
              </div>
            </div>
          </a>
        </div>
      </ProjectCard>
    </div>
  );
};

export default Projects;
